/**
 * Public parent packet — token auth. Plain letter text (API form_text) + fillable fields; no in-flow PDF embeds.
 */
import React, {
  useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { API_BASE_URL } from '../services/api';
import {
  CALENDAR_ICS_BUTTON_LABEL,
  isCalendarPacketForm,
  parentCalendarIcsHref,
  parentCalendarPdfHref,
  WOODMERE_SUMMER_ICS_PATH,
} from './parentPacketCalendar';
import NaToggle, { getPacketSiteSlug, isNaToggleEnabledSite } from './NaToggle';
import Fa16SocialHistoryFields, { initialFa16Fields } from './Fa16SocialHistoryFields';
import Fa17FeedingQuestionnaireFields, { initialFa17Fields } from './Fa17FeedingQuestionnaireFields';
import Fa12SocialHistoryFields, {
  initialFa12FieldsFromPacket,
  buildFa12SubmitPayload,
} from './Fa12SocialHistoryFields';
import {
  normalizeFa03BusRows,
  submitFieldsValid,
  validateForSubmit,
  packetFieldId,
  shouldShowRequiredFieldsCaption,
} from './parentPacketValidation';

const FA08_AFFIRM_KEYS = [
  'consent_video',
  'consent_video_behind',
  'consent_audio',
  'consent_photo',
  'consent_photo_blurred',
  'consent_social_media',
];

/** form_1 / form_01 → 1 */
function formIdToNum(formId) {
  const m = /^form_(\d+)$/i.exec(formId || '');
  return m ? parseInt(m[1], 10) : -1;
}

function isFaForm(formId) {
  return typeof formId === 'string' && formId.startsWith('fa_');
}

function isWmForm(formId) {
  return typeof formId === 'string' && formId.startsWith('wm_');
}

function packetDraftStorageKey(token, formId) {
  return `packet_draft_${token}_${formId}`;
}

function familyFlagsFromPacket(packet) {
  const p = packet || {};
  return {
    is_single_parent: !!p.is_single_parent,
    has_mother: p.has_mother !== false,
    has_father: p.has_father !== false,
    has_guardian: p.has_guardian === true,
  };
}

function familyStructureEditingLabel(flags) {
  const bits = [];
  if (flags.is_single_parent) bits.push('Single parent');
  if (flags.has_mother) bits.push('Mother');
  if (flags.has_father) bits.push('Father');
  if (flags.has_guardian) bits.push('Guardian');
  return bits.join(' — ') || '…';
}

function CalendarFormActions({ formId, siteSlug, token }) {
  if (!isCalendarPacketForm(formId, siteSlug)) return null;
  const pdfHref = parentCalendarPdfHref({ siteSlug, token, apiBaseUrl: API_BASE_URL });
  const icsHref = parentCalendarIcsHref({ token, apiBaseUrl: API_BASE_URL });
  if (!pdfHref && !icsHref) return null;
  return (
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {pdfHref && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<PictureAsPdfIcon />}
          href={pdfHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          View school calendar (PDF)
        </Button>
      )}
      {icsHref && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EventAvailableIcon />}
          href={icsHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {CALENDAR_ICS_BUTTON_LABEL}
        </Button>
      )}
      {formId === 'wm_16' && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EventAvailableIcon />}
          href={WOODMERE_SUMMER_ICS_PATH}
          target="_blank"
          rel="noopener noreferrer"
        >
          Summer 2026 calendar (.ics)
        </Button>
      )}
    </Box>
  );
}

function isInformationalPacketForm(formId) {
  if (isFaForm(formId)) {
    return formId === 'fa_01' || formId === 'fa_02' || formId === 'fa_15';
  }
  if (isWmForm(formId)) {
    return formId === 'wm_01';
  }
  const n = formIdToNum(formId);
  return (n >= 1 && n <= 6) || n === 16 || n === 17;
}

/** Full letter text (API form_text); Rockland 01–06 + calendar/meals/OCFS; 14th Ave fa_01, fa_02, fa_15 */
function informationalTextOnlyForm(formId) {
  if (formId === 'fa_01' || formId === 'fa_02' || formId === 'fa_15') return true;
  if (isWmForm(formId)) return formId === 'wm_01';
  const n = formIdToNum(formId);
  return [1, 2, 3, 4, 5, 6, 16, 17].includes(n);
}

function formHasParentFields(form) {
  if (!form) return false;
  if (form.has_parent_fields === true) return true;
  return !isInformationalPacketForm(form.form_id);
}

const PLACEHOLDER_EMERGENCY_CONTACT_NAMES = new Set([
  'parent/guardian of student',
  'parent/guardian',
]);

function sanitizeEmergencyContactPrefill(name) {
  const n = (name || '').trim();
  if (!n) return '';
  if (PLACEHOLDER_EMERGENCY_CONTACT_NAMES.has(n.toLowerCase())) return '';
  return n;
}

function initialFieldsForForm(formId, packet) {
  const p = packet || {};
  const base = { confirm_agreed: false };
  const mp = p.medical_profile || {};
  const ecs = p.emergency_contacts || [];
  const pps = p.pickup_persons || [];
  const consent = p.consent || {};
  const rows = p.allergy_rows || [];

  switch (formId) {
    case 'form_07':
      return {
        ...base,
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        home_address: p.home_address || '',
        email: p.parent_email || '',
        allergies: p.allergies || '',
        medication_school: mp.medications_text || '',
        medication_home: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        guardian_name: p.guardian_name || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
      };
    case 'form_08':
      return {
        ...base,
        visual_impairment: mp.has_visual_impairment || false,
        glasses: mp.wears_glasses || false,
        ear_infection: mp.has_ear_infection || false,
        ear_tubes: mp.has_ear_tubes || false,
        hearing_impairment: mp.has_hearing_impairment || false,
        seizure: mp.has_seizure_disorder || false,
        gtube: mp.has_gtube || false,
        asthma: mp.has_asthma || false,
        respiratory_compromise: mp.has_respiratory_compromise || false,
        cardiac_compromise: mp.has_cardiac_compromise || false,
        skin_condition: mp.has_skin_condition || false,
        dental_condition: mp.has_dental_condition || false,
        tracheostomy: mp.has_tracheostomy || false,
        oxygen: mp.has_oxygen || false,
        oximeter_monitoring: mp.requires_oximeter_monitoring || false,
        can_food_by_mouth: mp.can_have_food_by_mouth || false,
        can_food_by_mouth_liquids: mp.food_by_mouth_liquids || false,
        can_food_by_mouth_solids: mp.food_by_mouth_solids || false,
        problems_eating_drinking_yes: mp.problems_eating_drinking === true,
        problems_eating_drinking_no: mp.problems_eating_drinking === false,
        problems_eating_drinking_specify: mp.problems_eating_drinking_specify || '',
        medications: mp.medications_text || '',
        other_medical: mp.other_medical_notes || '',
      };
    case 'form_09':
      return {
        ...base,
        allergy_rows: rows.length > 0
          ? rows.map((r) => ({
            allergen: r.allergen || '',
            eat: r.reacts_to_eat || false,
            touch: r.reacts_to_touch || false,
            smell: r.reacts_to_smell || false,
            reaction: r.reaction_description || '',
          }))
          : Array(5).fill(null).map(() => ({
            allergen: '', eat: false, touch: false, smell: false, reaction: '',
          })),
        food_from_home: mp.food_from_home || '',
        liquid_type: mp.liquid_type || '',
      };
    case 'form_10':
      return {
        ...base,
        nickname: p.student_nickname || '',
        hair_color: p.student_hair_color || '',
        eye_color: p.student_eye_color || '',
        glasses: p.student_glasses || '',
        physical_description: p.student_physical_description || '',
        usual_behavior: p.student_usual_behavior || '',
        communication_ability: p.student_communication_ability || '',
        favorite_locations: p.student_favorite_locations || '',
      };
    case 'form_13':
      return {
        ...base,
        c_classroom: consent.consent_classroom || false,
        c_newsletters: consent.consent_newsletters || false,
        c_hallways: consent.consent_hallways || false,
        c_website: consent.consent_website || false,
        c_flyers: consent.consent_flyers || false,
        c_slideshow_building: consent.consent_slideshow_building || false,
        c_class_list: consent.consent_class_list || false,
        c_yearbook: consent.consent_yearbook || false,
        comm_sms: consent.comm_sms || false,
        comm_email: consent.comm_email || false,
        comm_voice: consent.comm_voice || false,
        comm_data_rates_ack: consent.comm_data_rates_ack || false,
      };
    case 'form_14':
      return {
        ...base,
        p1_name: pps[0]?.name || '',
        p1_phone: pps[0]?.phone || '',
        p2_name: pps[1]?.name || '',
        p2_phone: pps[1]?.phone || '',
        p3_name: pps[2]?.name || '',
        p3_phone: pps[2]?.phone || '',
      };
    case 'form_15':
      return { ...base };
    case 'form_11':
    case 'form_12':
    case 'form_18':
      return { ...base };
    case 'form_19':
      return {
        ...base,
        handbook_received_ack: false,
        handbook_illness_policy_ack: false,
        handbook_therapy_makeup_ack: false,
      };
    case 'fa_03': {
      const bus = [];
      for (let i = 0; i < 5; i += 1) {
        const rawName = (pps[i]?.name || '').trim();
        const parts = rawName.split(/\s+/).filter(Boolean);
        bus.push({
          first_name: parts[0] || '',
          last_name: parts.slice(1).join(' ') || '',
          relationship: '',
          address: pps[i]?.address || '',
          phone: pps[i]?.phone || '',
        });
      }
      return { ...base, bus_pickup_rows: bus };
    }
    case 'fa_04':
      return {
        ...base,
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        home_address: p.home_address || '',
        email: p.parent_email || '',
        allergies: p.allergies || '',
        medications_school: mp.medications_text || '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_relationship: ecs[2]?.relationship || '',
        ec3_phone: ecs[2]?.phone || '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        health_additional_notes: mp.other_medical_notes || '',
        emergency_treatment_ack: false,
        guardian_name: p.guardian_name || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
      };
    case 'fa_05':
      return {
        ...base,
        child_first_name: p.student_first_name || '',
        child_last_name: p.student_last_name || '',
        child_dob: p.student_dob || '',
        resides_parents: false,
        resides_parents_names: '',
        resides_guardian: false,
        guardian_name: p.guardian_name || '',
        resides_residence: false,
        residence_name: '',
        student_address: p.home_address || '',
        student_city: '',
        student_state: '',
        student_zip: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        email: p.parent_email || '',
        parent_phone: p.parent_cell || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
        medicaid_number: p.child_insurance_id || '',
        other_insurance_company: '',
        other_insurance_policy: '',
        parent_physical_diagnosis: '',
        parent_intellectual_diagnosis: '',
        allergy_food: '',
        allergy_drug: '',
        allergy_other: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_relationship: ecs[2]?.relationship || '',
        ec3_phone: ecs[2]?.phone || '',
        otc_consent_ack: false,
      };
    case 'fa_06':
      return {
        ...base,
        na_no_meds_or_feedings: false,
        medication_notes: mp.medications_text || '',
        understands_labeled_bottles: false,
      };
    case 'fa_07':
      return {
        ...base,
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        doctor_comm_ack: false,
        release_info_ack: false,
      };
    case 'fa_08':
      return {
        ...base,
        consent_video: !!consent.consent_video,
        consent_video_behind: !!consent.consent_video_behind,
        consent_audio: !!consent.consent_audio,
        consent_photo: !!consent.consent_photo,
        consent_photo_blurred: !!consent.consent_photo_blurred,
        consent_social_media: !!consent.consent_social_media,
        consent_none: !!consent.consent_none,
      };
    case 'bs_08':
      return {
        ...base,
        consent_video: !!consent.consent_video,
        consent_audio: !!consent.consent_audio,
        consent_photo: !!consent.consent_photo,
        consent_website: !!consent.consent_website,
      };
    case 'fa_09':
      return {
        ...base,
        remote_classroom: !!consent.remote_learning_classroom,
        remote_speech: !!consent.remote_learning_speech,
        remote_ot: !!consent.remote_learning_ot,
        remote_pt: !!consent.remote_learning_pt,
        remote_counseling: !!consent.remote_learning_counseling,
      };
    case 'fa_10':
      return { ...base, trips_ack: false };
    case 'fa_11':
      return {
        ...base,
        sc_name: p.service_coordinator_name || '',
        sc_agency: p.service_coordinator_agency || '',
        sc_phone: p.service_coordinator_phone || '',
      };
    case 'fa_12':
      return {
        ...base,
        ...initialFa12FieldsFromPacket(p.social_history_data || {}),
      };
    case 'fa_16':
      return { ...base, ...initialFa16Fields() };
    case 'fa_17':
      return { ...base, ...initialFa17Fields() };
    case 'wm_02':
    case 'wm_13':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        medicaid_cin: '',
      };
    case 'wm_03':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        gender: '',
        home_address: p.home_address || '',
        home_phone: p.parent_phone || p.mother_cell || p.father_cell || '',
        child_lives_with: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        mother_work_phone: p.mother_work_phone || '',
        mother_work_address: p.mother_work_address || '',
        email: p.parent_email || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        father_work_phone: p.father_work_phone || '',
        father_work_address: p.father_work_address || '',
        father_email: p.father_email || p.parent_email || '',
        primary_language: '',
        interpreter_needed: '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        allergies: p.allergies || '',
        medications_yesno: '',
        medications_detail: '',
        insurance_name: p.insurance_type || '',
        insurance_id: p.child_insurance_id || '',
        insurance_group: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_home_phone: '',
        ec1_cell_phone: ecs[0]?.phone || '',
        ec1_address: ecs[0]?.address || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_home_phone: '',
        ec2_cell_phone: ecs[1]?.phone || '',
        ec2_address: ecs[1]?.address || '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_home_phone: '',
        ec3_cell_phone: ecs[2]?.phone || '',
        ec3_address: ecs[2]?.address || '',
      };
    case 'wm_04':
    case 'wm_06':
      return { ...base };
    case 'wm_05':
      return {
        ...base,
        consent_video: false,
        consent_audio: false,
        consent_photo: false,
        consent_none: false,
      };
    case 'wm_07':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        medication_name: '',
        medication_dosage: '',
        medication_frequency: '',
        medication_2_name: '',
        medication_2_dosage: '',
        medication_2_frequency: '',
      };
    case 'wm_09':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        medicaid_cin: '',
      };
    case 'wm_10': {
      const authFields = {};
      for (let i = 1; i <= 4; i += 1) {
        authFields[`auth${i}_name`] = '';
        authFields[`auth${i}_cell`] = '';
        authFields[`auth${i}_vehicle`] = '';
        authFields[`auth${i}_plate`] = '';
      }
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        home_address: p.home_address || '',
        home_city: '',
        home_zip: '',
        school_district: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        ...authFields,
      };
    }
    case 'wm_11':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
      };
    case 'wm_12':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        alert_description: '',
      };
    case 'wm_14':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
      };
    default:
      return { ...base };
  }
}

function FormFields({
  formId, fields, setF, packet, fieldErrors = {}, clearFieldError, readOnly = false,
}) {
  const showMother = packet?.has_mother !== false;
  const showFather = packet?.has_father !== false;
  const showGuardian = packet?.has_guardian === true;
  const err = (key) => fieldErrors[key];
  const onText = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.value);
  };
  const onCheck = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.checked);
  };
  const reqStar = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
  );
  const row = (label, key, props = {}, opts = {}) => {
    const { required: isRequired, phone: isPhone } = opts;
    const emsg = err(key);
    return (
      <TextField
        key={key}
        id={packetFieldId(key)}
        fullWidth
        required={!!isRequired && !readOnly}
        label={label}
        value={fields[key] ?? ''}
        onChange={onText(key)}
        margin="normal"
        size="small"
        error={!!emsg}
        helperText={emsg || (isPhone && !emsg ? 'Leave blank or enter a 10-digit phone number' : undefined)}
        InputProps={{ readOnly }}
        disabled={readOnly}
        {...props}
      />
    );
  };
  const cb = (label, key) => (
    <FormControlLabel
      key={key}
      control={(
        <Checkbox
          checked={!!fields[key]}
          onChange={onCheck(key)}
          disabled={readOnly}
        />
      )}
      label={label}
    />
  );
  const cbReq = (label, key, boxSx = {}) => (
    <Box
      key={key}
      id={packetFieldId(key)}
      sx={{
        ...(err(key) ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : {}),
        ...boxSx,
      }}
    >
      <FormControlLabel
        control={(
          <Checkbox
            checked={!!fields[key]}
            onChange={onCheck(key)}
            color={err(key) ? 'error' : 'default'}
            disabled={readOnly}
          />
        )}
        label={(
          <>
            {label}
            {reqStar}
          </>
        )}
      />
      {!!err(key) && (
        <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
          {err(key)}
        </Typography>
      )}
    </Box>
  );

  if (formId === 'form_07') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Child (from school record)</Typography>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {showMother && (
          <>
            {row('Mother name', 'mother_name')}
            {row('Mother cell', 'mother_cell', {}, { phone: true })}
          </>
        )}
        {showFather && (
          <>
            {row('Father name', 'father_name')}
            {row('Father cell', 'father_cell', {}, { phone: true })}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: showMother || showFather ? 1 : 0 }}>Guardian</Typography>
            {row('Guardian name', 'guardian_name')}
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child (e.g. grandmother, step-parent)', 'guardian_relationship')}
          </>
        )}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Email', 'email')}
        {row('Allergies', 'allergies', { multiline: true, minRows: 2 })}
        {row('Medication at school', 'medication_school')}
        {row('Medication at home', 'medication_home')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 1</Typography>
        {row('Name', 'ec1_name')}
        {row('Relationship', 'ec1_relationship')}
        {row('Phone', 'ec1_phone', {}, { phone: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 2</Typography>
        {row('Name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Phone', 'ec2_phone', {}, { phone: true })}
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Dentist name', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
      </>
    );
  }
  if (formId === 'form_08') {
    const setProblemsAnswer = (answer) => {
      if (clearFieldError) clearFieldError('problems_eating_drinking_specify');
      if (answer === 'yes') {
        setF('problems_eating_drinking_yes', true);
        setF('problems_eating_drinking_no', false);
      } else {
        setF('problems_eating_drinking_yes', false);
        setF('problems_eating_drinking_no', true);
      }
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {cb('Visual impairment', 'visual_impairment')}
        {cb('Glasses', 'glasses')}
        {cb('History of ear infection', 'ear_infection')}
        {cb('Ear tubes', 'ear_tubes')}
        {cb('Hearing impairment', 'hearing_impairment')}
        {cb('Seizure history', 'seizure')}
        {cb('G-tube', 'gtube')}
        {cb('Asthma', 'asthma')}
        {cb('Respiratory compromise', 'respiratory_compromise')}
        {cb('Cardiac compromise', 'cardiac_compromise')}
        {cb('Skin condition', 'skin_condition')}
        {cb('Dental condition', 'dental_condition')}
        {cb('Tracheostomy', 'tracheostomy')}
        {cb('Oxygen', 'oxygen')}
        {cb('Requires oximeter monitoring', 'oximeter_monitoring')}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2">G-tube / feeding</Typography>
        {cb('Can have food by mouth', 'can_food_by_mouth')}
        {!!fields.can_food_by_mouth && (
          <Box sx={{ pl: 2 }}>
            {cb('Liquids', 'can_food_by_mouth_liquids')}
            {cb('Solids', 'can_food_by_mouth_solids')}
          </Box>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2">Problems with eating or drinking</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.problems_eating_drinking_yes}
                onChange={() => setProblemsAnswer('yes')}
              />
            )}
            label="Yes"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.problems_eating_drinking_no}
                onChange={() => setProblemsAnswer('no')}
              />
            )}
            label="No"
          />
        </Box>
        {!!fields.problems_eating_drinking_yes && (
          <TextField
            fullWidth
            id={packetFieldId('problems_eating_drinking_specify')}
            label="If yes, specify"
            value={fields.problems_eating_drinking_specify || ''}
            onChange={(e) => {
              if (clearFieldError) clearFieldError('problems_eating_drinking_specify');
              setF('problems_eating_drinking_specify', e.target.value);
            }}
            margin="normal"
            size="small"
            multiline
            minRows={2}
            error={!!err('problems_eating_drinking_specify')}
            helperText={err('problems_eating_drinking_specify')}
          />
        )}
        <TextField
          fullWidth
          label="Medications"
          value={fields.medications || ''}
          onChange={(e) => setF('medications', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
        <TextField
          fullWidth
          label="Other medical needs"
          value={fields.other_medical || ''}
          onChange={(e) => setF('other_medical', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
      </>
    );
  }
  if (formId === 'form_09') {
    const rows = fields.allergy_rows || [];
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption">Allergen row {i + 1}</Typography>
            <TextField
              fullWidth
              size="small"
              label="Allergen / restriction"
              value={rows[i]?.allergen || ''}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...next[i], allergen: e.target.value };
                setF('allergy_rows', next);
              }}
              margin="dense"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['eat', 'touch', 'smell'].map((k) => (
                <FormControlLabel
                  key={k}
                  control={
                    <Checkbox
                      checked={!!rows[i]?.[k]}
                      onChange={(e) => {
                        const next = [...rows];
                        next[i] = { ...next[i], [k]: e.target.checked };
                        setF('allergy_rows', next);
                      }}
                    />
                  }
                  label={k}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Reaction"
              value={rows[i]?.reaction || ''}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...next[i], reaction: e.target.value };
                setF('allergy_rows', next);
              }}
              margin="dense"
            />
          </Box>
        ))}
        {row('Food from home (yes/no / notes)', 'food_from_home')}
        {row('Liquid type', 'liquid_type')}
      </>
    );
  }
  if (formId === 'form_10') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {row('Nickname', 'nickname')}
        {row('Hair color', 'hair_color', {}, { required: true })}
        {row('Eye color', 'eye_color', {}, { required: true })}
        {row('Glasses (yes/no)', 'glasses', {}, { required: true })}
        {row('Physical description', 'physical_description', { multiline: true, minRows: 3 }, { required: true })}
        {row('Usual behavior', 'usual_behavior', { multiline: true, minRows: 2 }, { required: true })}
        {row('Communication ability', 'communication_ability', { multiline: true, minRows: 2 }, { required: true })}
        {row('Favorite locations', 'favorite_locations', {}, { required: true })}
      </>
    );
  }
  if (formId === 'form_11') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Over-the-counter medication consent
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By submitting this page, you authorize HASC staff to administer over-the-counter medications
          to your child as directed by your child&apos;s pediatrician and documented on the signed medical form.
          This consent remains in effect for the current school year unless you submit an updated instruction.
        </Typography>
      </>
    );
  }
  if (formId === 'form_12') {
    return (
      <Typography variant="body2" color="text.secondary">
        {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        <br />
        Pediatrician section will be completed separately (Phase 6).
      </Typography>
    );
  }
  if (formId === 'form_13') {
    const items = [
      ['c_classroom', 'Classroom use (projects, Smartboards, bulletin boards)'],
      ['c_newsletters', 'Newsletters to classmates'],
      ['c_hallways', 'Photo/video in hallways'],
      ['c_website', 'Website / informational / fundraising'],
      ['c_flyers', 'Brochures / flyers'],
      ['c_slideshow_building', 'Slideshow in school building'],
      ['c_class_list', 'Name on class list'],
      ['c_yearbook', 'End-of-year slideshow / yearbook'],
    ];
    const commItems = [
      [
        'comm_sms',
        'I consent to receive text messages (SMS) from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_email',
        'I consent to receive emails from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_voice',
        'I consent to receive automated voice calls from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_data_rates_ack',
        'I understand that message and data rates may apply for text messages, and I may opt out at any time by replying STOP.',
      ],
    ];
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {items.map(([k, lab]) => (
          <FormControlLabel
            key={k}
            control={<Checkbox checked={!!fields[k]} onChange={(e) => setF(k, e.target.checked)} />}
            label={lab}
          />
        ))}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Communication Consent
        </Typography>
        {commItems.map(([k, lab]) => (
          <FormControlLabel
            key={k}
            sx={{ alignItems: 'flex-start', mr: 0 }}
            control={
              <Checkbox
                checked={!!fields[k]}
                onChange={(e) => setF(k, e.target.checked)}
                sx={{ pt: 0.25 }}
              />
            }
            label={<Typography variant="body2">{lab}</Typography>}
          />
        ))}
      </>
    );
  }
  if (formId === 'form_14') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Child: {packet.student_first_name} {packet.student_last_name} — Parent: {packet.parent_name || '—'}
        </Typography>
        <Typography variant="subtitle2">Authorized pickup 1</Typography>
        {row('Name', 'p1_name')}
        {row('Phone', 'p1_phone', {}, { phone: true })}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 2</Typography>
        {row('Name', 'p2_name')}
        {row('Phone', 'p2_phone', {}, { phone: true })}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 3</Typography>
        {row('Name', 'p3_name')}
        {row('Phone', 'p3_phone', {}, { phone: true })}
      </>
    );
  }
  if (formId === 'form_15') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — {packet.parent_name || '—'}
        </Typography>
        {cbReq(
          'I authorize the release of medical, educational, therapy, and prescription information as described in the form above',
          'release_consent_ack',
          { mt: 2 },
        )}
      </>
    );
  }
  if (formId === 'form_18') {
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name}
          {packet.student_dob && ` — DOB ${packet.student_dob}`}
        </Typography>
        {cbReq('I have read and understand the illness policy', 'confirm_policy', { mt: 2 })}
      </>
    );
  }
  if (formId === 'form_19') {
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name}
          {` — ${packet.parent_name || ''}`}
          {packet.student_dob && ` — DOB ${packet.student_dob}`}
        </Typography>
        {cbReq(
          'I have received a copy of the HASC Parent Handbook. I reviewed the information, and I am aware that a file is maintained on my child and it is my right to view this file at any time.',
          'handbook_received_ack',
          { mt: 2 },
        )}
        {cbReq(
          'I have reviewed the Illness Policy, and understand its contents.',
          'handbook_illness_policy_ack',
          { mt: 1 },
        )}
        {cbReq(
          'I understand that the therapy team will make every effort or attempt to make up missed therapy sessions.',
          'handbook_therapy_makeup_ack',
          { mt: 1 },
        )}
      </>
    );
  }

  if (formId === 'fa_03') {
    const rows = fields.bus_pickup_rows || [];
    const ensureRows = () => (rows.length ? [...rows] : Array(5).fill(null).map(() => ({})));
    const patchRow = (i, patch) => {
      const next = ensureRows();
      next[i] = { ...(next[i] || {}), ...patch };
      setF('bus_pickup_rows', next);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Authorized persons 1–3: enter first name, last name, and phone for each. Persons 4–5 are optional.
        </Typography>
        {[0, 1, 2, 3, 4].map((i) => {
          const fa03k = (part) => `fa03_bus_${i}_${part}`;
          return (
          <Box key={i} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption">Authorized person {i + 1}</Typography>
            <TextField
              fullWidth
              size="small"
              required={i < 3}
              id={packetFieldId(fa03k('first'))}
              label="First name"
              value={rows[i]?.first_name || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('first'));
                patchRow(i, { first_name: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('first'))}
              helperText={err(fa03k('first'))}
            />
            <TextField
              fullWidth
              size="small"
              required={i < 3}
              id={packetFieldId(fa03k('last'))}
              label="Last name"
              value={rows[i]?.last_name || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('last'));
                patchRow(i, { last_name: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('last'))}
              helperText={err(fa03k('last'))}
            />
            <TextField
              fullWidth
              size="small"
              label="Relationship (optional)"
              value={rows[i]?.relationship || ''}
              onChange={(e) => patchRow(i, { relationship: e.target.value })}
              margin="dense"
            />
            <TextField
              fullWidth
              size="small"
              label="Address (optional)"
              margin="dense"
              value={rows[i]?.address || ''}
              onChange={(e) => patchRow(i, { address: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              required={i < 3}
              id={packetFieldId(fa03k('phone'))}
              label="Phone"
              value={rows[i]?.phone || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('phone'));
                patchRow(i, { phone: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('phone'))}
              helperText={
                err(fa03k('phone'))
                || (i < 3 ? '10 digits required' : 'Leave blank or enter 10 digits')
              }
            />
          </Box>
          );
        })}
      </>
    );
  }

  if (formId === 'fa_04') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {showMother && (
          <>
            {row('Mother name', 'mother_name')}
            {row('Mother cell', 'mother_cell', {}, { phone: true })}
          </>
        )}
        {showFather && (
          <>
            {row('Father name', 'father_name')}
            {row('Father cell', 'father_cell', {}, { phone: true })}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: showMother || showFather ? 1 : 0 }}>Guardian</Typography>
            {row('Guardian name', 'guardian_name')}
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child (e.g. grandmother, step-parent)', 'guardian_relationship')}
          </>
        )}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Email', 'email')}
        {row('Allergies', 'allergies', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contacts</Typography>
        {row('Contact 1 name', 'ec1_name')}
        {row('Relationship', 'ec1_relationship')}
        {row('Phone', 'ec1_phone', {}, { phone: true })}
        {row('Contact 2 name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Phone', 'ec2_phone', {}, { phone: true })}
        {row('Contact 3 name', 'ec3_name')}
        {row('Relationship', 'ec3_relationship')}
        {row('Phone', 'ec3_phone', {}, { phone: true })}
        {row('Pediatrician', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Dentist', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
        {row('Medications at school', 'medications_school', { multiline: true, minRows: 2 })}
        {row('Additional health notes', 'health_additional_notes', { multiline: true, minRows: 3 })}
        {cbReq('I consent to emergency medical treatment as described in the form above', 'emergency_treatment_ack', { mt: 2 })}
      </>
    );
  }

  if (formId === 'fa_05') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Child (correct if needed)</Typography>
        {row('Legal first name', 'child_first_name')}
        {row('Legal last name', 'child_last_name')}
        {row('Date of birth (YYYY-MM-DD)', 'child_dob')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Student resides with (check all that apply)</Typography>
        {cb('Parent(s)', 'resides_parents')}
        {row('Name(s) of parent(s)', 'resides_parents_names')}
        {cb('Guardian', 'resides_guardian')}
        {row('Guardian name', 'guardian_name')}
        {cb('Residence (name of residence)', 'resides_residence')}
        {row('Residence name', 'residence_name')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Student address</Typography>
        {row('Street address', 'student_address', { multiline: true, minRows: 2 })}
        {row('City', 'student_city')}
        {row('State', 'student_state')}
        {row('ZIP', 'student_zip')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Parent / guardian contact</Typography>
        {showMother && (
          <>
            {row('Mother name', 'mother_name')}
            {row('Mother cell', 'mother_cell', {}, { phone: true })}
          </>
        )}
        {showFather && (
          <>
            {row('Father name', 'father_name')}
            {row('Father cell', 'father_cell', {}, { phone: true })}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Guardian (completing this packet)</Typography>
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child', 'guardian_relationship')}
          </>
        )}
        {row('Email', 'email')}
        {row('Primary phone (form line)', 'parent_phone', {}, { phone: true })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Insurance</Typography>
        {row('Medicaid #', 'medicaid_number')}
        {row('Other health insurance — company', 'other_insurance_company')}
        {row('Other health insurance — policy #', 'other_insurance_policy')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Diagnoses (parent attestation)</Typography>
        {row('Physical diagnosis', 'parent_physical_diagnosis')}
        {row('Intellectual diagnosis', 'parent_intellectual_diagnosis')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Allergies (parent attestation)</Typography>
        {row('Food', 'allergy_food')}
        {row('Drug', 'allergy_drug')}
        {row('Other', 'allergy_other', { multiline: true, minRows: 2 })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Emergency contacts</Typography>
        {row('Contact 1 name', 'ec1_name')}
        {row('Relationship', 'ec1_relationship')}
        {row('Phone', 'ec1_phone', {}, { phone: true })}
        {row('Contact 2 name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Phone', 'ec2_phone', {}, { phone: true })}
        {row('Contact 3 name', 'ec3_name')}
        {row('Relationship', 'ec3_relationship')}
        {row('Phone', 'ec3_phone', {}, { phone: true })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Page 2 of the medical form covers over-the-counter medication consent. Check below to give
          permission for OTC medications as your doctor orders on the printed form (your ink signature is on that page).
        </Typography>
        {cbReq('I consent to OTC medications as described on the medical form', 'otc_consent_ack', { mt: 1 })}
      </>
    );
  }

  if (formId === 'fa_06') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name}
        </Typography>
        <NaToggle
          label="My child does not take medications or have GTube feedings at school"
          checked={!!fields.na_no_meds_or_feedings}
          onChange={(next) => setF('na_no_meds_or_feedings', next)}
          targets={['medication_notes']}
          setF={setF}
        />
        {!fields.na_no_meds_or_feedings && row('Medication notes (from school record)', 'medication_notes', { multiline: true, minRows: 2 })}
        {fields.na_no_meds_or_feedings && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Medication detail fields are not applicable. You can still submit with the policy acknowledgment below.
          </Typography>
        )}
        {cbReq('I understand medications must be in labeled prescription bottles', 'understands_labeled_bottles', { mt: 1 })}
      </>
    );
  }

  if (formId === 'fa_07') {
    return (
      <>
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Dentist name', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
          Consent for communication with student&apos;s doctors
        </Typography>
        {cbReq("I consent to communication with my child's doctors and healthcare providers", 'doctor_comm_ack', { mt: 0.5 })}
        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
          Consent to release information to authorized personnel and agencies
        </Typography>
        {cbReq('I consent to the release of information to authorized personnel and agencies', 'release_info_ack', { mt: 0.5 })}
      </>
    );
  }

  if (formId === 'fa_08') {
    const setAffirm = (key) => (e) => {
      const checked = e.target.checked;
      if (clearFieldError) clearFieldError('consent_none');
      if (checked) setF('consent_none', false);
      setF(key, checked);
    };
    const setNone = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) clearFieldError('consent_none');
      if (checked) {
        FA08_AFFIRM_KEYS.forEach((k) => setF(k, false));
      }
      setF('consent_none', checked);
    };
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          I hereby consent to the following (check all that apply):
        </Typography>
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_video} onChange={setAffirm('consent_video')} disabled={readOnly} />} label="Video taping" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_video_behind} onChange={setAffirm('consent_video_behind')} disabled={readOnly} />} label="Video taping from behind so face is obscured" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_audio} onChange={setAffirm('consent_audio')} disabled={readOnly} />} label="Audio taping" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_photo} onChange={setAffirm('consent_photo')} disabled={readOnly} />} label="Photographing" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_photo_blurred} onChange={setAffirm('consent_photo_blurred')} disabled={readOnly} />} label="Photographing with blurred face or from behind" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_social_media} onChange={setAffirm('consent_social_media')} disabled={readOnly} />} label="Use on website or social media (Instagram)" />
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'error.main' }}>
          If you do not consent to video or photography
        </Typography>
        <Box id={packetFieldId('consent_none')} sx={err('consent_none') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.consent_none}
                onChange={setNone}
                disabled={readOnly}
                color={err('consent_none') ? 'error' : 'default'}
              />
            )}
            label={(
              <>
                I DO NOT consent to video or photography of my child
                {reqStar}
              </>
            )}
            sx={{ color: 'error.main' }}
          />
          {!!err('consent_none') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
              {err('consent_none')}
            </Typography>
          )}
        </Box>
      </>
    );
  }

  if (formId === 'bs_08') {
    const setMediaConsent = (key) => (e) => {
      if (clearFieldError) clearFieldError('consent_video');
      setF(key, e.target.checked);
    };
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Media consent options (check all that apply):
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_video}
              onChange={setMediaConsent('consent_video')}
              disabled={readOnly}
              color={err('consent_video') ? 'error' : 'default'}
            />
          )}
          label="Video recording"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_audio}
              onChange={setMediaConsent('consent_audio')}
              disabled={readOnly}
            />
          )}
          label="Audio recording"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_photo}
              onChange={setMediaConsent('consent_photo')}
              disabled={readOnly}
            />
          )}
          label="Photographs"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_website}
              onChange={setMediaConsent('consent_website')}
              disabled={readOnly}
            />
          )}
          label="Use on school website / public materials"
        />
        {!!err('consent_video') && (
          <Typography
            id={packetFieldId('consent_video')}
            variant="caption"
            color="error"
            component="div"
            sx={{ mt: 0.5 }}
          >
            {err('consent_video')}
          </Typography>
        )}
      </>
    );
  }

  if (formId === 'fa_09') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          I consent to have my child receive the following services via remote learning and teletherapy:
        </Typography>
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_classroom} onChange={(e) => setF('remote_classroom', e.target.checked)} />} label="Remote Learning with classroom teacher" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_speech} onChange={(e) => setF('remote_speech', e.target.checked)} />} label="Speech Therapy with assigned provider" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_ot} onChange={(e) => setF('remote_ot', e.target.checked)} />} label="Occupational Therapy with assigned provider" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_pt} onChange={(e) => setF('remote_pt', e.target.checked)} />} label="Physical Therapy with assigned provider" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_counseling} onChange={(e) => setF('remote_counseling', e.target.checked)} />} label="Counseling Services with assigned provider" />
      </>
    );
  }

  if (formId === 'fa_10') {
    return (
      <>
        {cb('I consent to school trips and neighborhood walks as described', 'trips_ack')}
      </>
    );
  }

  if (formId === 'fa_11') {
    return (
      <>
        {row('Service coordinator name', 'sc_name')}
        {row('Agency', 'sc_agency')}
        {row('Telephone', 'sc_phone', {}, { phone: true })}
      </>
    );
  }

  if (formId === 'fa_12') {
    return (
      <Fa12SocialHistoryFields
        fields={fields}
        setF={setF}
        packet={packet}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        readOnly={readOnly}
      />
    );
  }

  if (formId === 'fa_16') {
    return (
      <Fa16SocialHistoryFields
        fields={fields}
        setF={setF}
        packet={packet}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
      />
    );
  }

  if (formId === 'fa_17') {
    return (
      <Fa17FeedingQuestionnaireFields
        fields={fields}
        setF={setF}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
      />
    );
  }

  if (formId === 'wm_02' || formId === 'wm_13') {
    return (
      <>
        {row('Parent / guardian name', 'parent_name')}
        {row('Medicaid CIN', 'medicaid_cin', {}, { required: true })}
      </>
    );
  }

  if (formId === 'wm_03') {
    return (
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
        {row('Gender', 'gender')}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Home phone', 'home_phone', {}, { phone: true })}
        {row('Child lives with', 'child_lives_with')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Mother / guardian</Typography>
        {row('Mother name', 'mother_name')}
        {row('Mother cell', 'mother_cell', {}, { phone: true })}
        {row('Mother work phone', 'mother_work_phone', {}, { phone: true })}
        {row('Email', 'email')}
        {row('Mother work address', 'mother_work_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Father / guardian</Typography>
        {row('Father name', 'father_name')}
        {row('Father cell', 'father_cell', {}, { phone: true })}
        {row('Father work phone', 'father_work_phone', {}, { phone: true })}
        {row('Father email', 'father_email')}
        {row('Father work address', 'father_work_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Language and health</Typography>
        {row('Primary language', 'primary_language')}
        {row('Interpreter needed', 'interpreter_needed')}
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Allergies', 'allergies')}
        {row('Taking medications? (Yes/No)', 'medications_yesno')}
        {row('Medication details', 'medications_detail', { multiline: true, minRows: 2 })}
        {row('Insurance name', 'insurance_name')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance group', 'insurance_group')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 1</Typography>
        {row('Name', 'ec1_name', {}, { required: true })}
        {row('Relationship', 'ec1_relationship')}
        {row('Home phone', 'ec1_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec1_cell_phone', {}, { phone: true, required: true })}
        {row('Address', 'ec1_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 2</Typography>
        {row('Name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Home phone', 'ec2_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec2_cell_phone', {}, { phone: true })}
        {row('Address', 'ec2_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 3</Typography>
        {row('Name', 'ec3_name')}
        {row('Home phone', 'ec3_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec3_cell_phone', {}, { phone: true })}
        {row('Address', 'ec3_address', { multiline: true, minRows: 2 })}
      </>
    );
  }

  if (formId === 'wm_04' || formId === 'wm_06') {
    return null;
  }

  if (formId === 'wm_05') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Media consent options
        </Typography>
        {cb('Video recording', 'consent_video')}
        {cb('Audio recording', 'consent_audio')}
        {cb('Photographs', 'consent_photo')}
        {cbReq('I do not consent to any video/audio/photo recording', 'consent_none', { mt: 1 })}
      </>
    );
  }

  if (formId === 'wm_07') {
    return (
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Medication 1</Typography>
        {row('Medication name', 'medication_name', {}, { required: true })}
        {row('Dosage', 'medication_dosage')}
        {row('Frequency', 'medication_frequency')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Medication 2 (optional)</Typography>
        {row('Medication name', 'medication_2_name')}
        {row('Dosage', 'medication_2_dosage')}
        {row('Frequency', 'medication_2_frequency')}
      </>
    );
  }

  if (formId === 'wm_09') {
    return (
      <>
        {row('Parent / guardian name', 'parent_name')}
        {row('Medicaid CIN', 'medicaid_cin', {}, { required: true })}
      </>
    );
  }

  if (formId === 'wm_10') {
    const authBlock = (n) => (
      <>
        <Typography variant="subtitle2" sx={{ mt: n === 1 ? 1 : 2 }}>
          {`Authorized person ${n}${n === 1 ? '' : ' (optional)'}`}
        </Typography>
        {row('Name', `auth${n}_name`, {}, n === 1 ? { required: true } : {})}
        {row('Cell', `auth${n}_cell`, {}, n === 1 ? { required: true, phone: true } : { phone: true })}
        {row('Make / model', `auth${n}_vehicle`)}
        {row('License plate', `auth${n}_plate`)}
      </>
    );
    return (
      <>
        {row('Student name', 'student_name', { InputProps: { readOnly: true } })}
        {row('Date of birth', 'date_of_birth', { type: 'date', InputProps: { readOnly: true } })}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 }, { required: true })}
        {row('City', 'home_city', {}, { required: true })}
        {row('ZIP code', 'home_zip', {}, { required: true })}
        {row('School district', 'school_district', {}, { required: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Parent / guardian contact</Typography>
        {row('Mother name', 'mother_name', {}, { required: true })}
        {row('Mother cell', 'mother_cell', {}, { required: true, phone: true })}
        {row('Father name', 'father_name', {}, { required: true })}
        {row('Father cell', 'father_cell', {}, { required: true, phone: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Persons authorized to receive child</Typography>
        {authBlock(1)}
        {authBlock(2)}
        {authBlock(3)}
        {authBlock(4)}
      </>
    );
  }

  if (formId === 'wm_11') {
    return (
      <>
        {row('Student name', 'student_name')}
      </>
    );
  }

  if (formId === 'wm_12') {
    return (
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
        {row('Special alert description', 'alert_description', { multiline: true, minRows: 3 }, { required: true })}
      </>
    );
  }

  if (formId === 'wm_14') {
    return (
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
      </>
    );
  }

  return null;
}

export default function ParentPacketPage() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fields, setFields] = useState({});
  const [draftSavedAtMs, setDraftSavedAtMs] = useState(null);
  const fieldsBaselineJsonRef = useRef(null);
  const [familyPickerExpanded, setFamilyPickerExpanded] = useState(true);
  const [fsDraft, setFsDraft] = useState({
    is_single_parent: false,
    has_mother: true,
    has_father: true,
    has_guardian: false,
  });
  const [familySaving, setFamilySaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitFieldErrors, setSubmitFieldErrors] = useState([]);
  const [printingPacket, setPrintingPacket] = useState(false);
  const [printError, setPrintError] = useState('');

  const clearFieldError = useCallback((key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const fetchPacket = useCallback(async () => {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/`;
      const res = await fetch(url);
      if (!res.ok) {
        if (res.status === 404) throw new Error('invalid');
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load');
      }
      const data = await res.json();
      setPacket(data);
      const firstPending = (data.forms || []).findIndex((f) => f.status !== 'signed');
      setCurrentIndex(firstPending >= 0 ? firstPending : (data.forms || []).length);
    } catch (e) {
      setPacket(null);
      setError(e.message === 'invalid' ? null : e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPacket();
  }, [fetchPacket]);

  useEffect(() => {
    if (!packet) return;
    setFsDraft(familyFlagsFromPacket(packet));
  }, [packet]);

  useEffect(() => {
    if (!token) return;
    try {
      if (sessionStorage.getItem(`packet_fs_collapsed_${token}`) === '1') {
        setFamilyPickerExpanded(false);
      } else {
        setFamilyPickerExpanded(true);
      }
    } catch {
      /* ignore */
    }
  }, [token]);

  const saveFamilyStructure = useCallback(async () => {
    if (!token || !packet) return;
    if (!fsDraft.has_mother && !fsDraft.has_father && !fsDraft.has_guardian) {
      setError('Select at least one of Mother, Father, or Guardian.');
      return;
    }
    setError('');
    setFamilySaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/family-structure/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_single_parent: fsDraft.is_single_parent,
          has_mother: fsDraft.has_mother,
          has_father: fsDraft.has_father,
          has_guardian: fsDraft.has_guardian,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save family setup');
      }
      const updated = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/`).then((r) => r.json());
      setPacket(updated);
      setFamilyPickerExpanded(false);
      try {
        sessionStorage.setItem(`packet_fs_collapsed_${token}`, '1');
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setFamilySaving(false);
    }
  }, [token, packet, fsDraft]);

  const currentForm = packet?.forms?.[currentIndex];
  const formId = currentForm?.form_id;

  useEffect(() => {
    setFieldErrors({});
    setSubmitFieldErrors([]);
  }, [formId, currentIndex]);

  const packetSiteSlug = getPacketSiteSlug(packet);
  const naToggleEnabledForSite = isNaToggleEnabledSite(packetSiteSlug);
  const savedFormDataKey =
    currentForm?.saved_form_data != null && typeof currentForm.saved_form_data === 'object'
      ? JSON.stringify(currentForm.saved_form_data)
      : '';

  useLayoutEffect(() => {
    if (!packet || !formId || !token) return;

    if (currentForm?.status === 'signed') {
      const init = initialFieldsForForm(formId, packet);
      if (savedFormDataKey) {
        try {
          const saved = JSON.parse(savedFormDataKey);
          if (saved && typeof saved === 'object') {
            let merged;
            if (formId === 'fa_12') {
              merged = { ...initialFa12FieldsFromPacket(saved), confirm_agreed: true };
            } else {
              merged = { ...init, ...saved, confirm_agreed: true };
            }
            if (formId === 'fa_03' && merged.bus_pickup_rows) {
              merged = { ...merged, bus_pickup_rows: normalizeFa03BusRows(merged.bus_pickup_rows) };
            }
            if (formId === 'fa_07') {
              merged = {
                ...merged,
                doctor_comm_ack: !!(saved.doctor_comm_ack ?? saved.fa_07_doctor_ack ?? saved.release_ack),
                release_info_ack: !!(saved.release_info_ack ?? saved.fa_07_release_ack ?? saved.release_ack),
              };
            }
            setFields(merged);
            fieldsBaselineJsonRef.current = JSON.stringify(merged);
            setDraftSavedAtMs(null);
            return;
          }
        } catch {
          /* ignore */
        }
      }
      setFields(init);
      fieldsBaselineJsonRef.current = JSON.stringify(init);
      setDraftSavedAtMs(null);
      return;
    }

    const init = initialFieldsForForm(formId, packet);
    const hasPF = formHasParentFields(currentForm);

    if (hasPF) {
      try {
        const raw = localStorage.getItem(packetDraftStorageKey(token, formId));
        if (raw) {
          const parsed = JSON.parse(raw);
          const formState = parsed && typeof parsed === 'object' ? parsed.formState : null;
          if (formState && typeof formState === 'object' && !Array.isArray(formState)) {
            let merged = { ...init, ...formState };
            if (formId === 'fa_03' && merged.bus_pickup_rows) {
              const norm = normalizeFa03BusRows(merged.bus_pickup_rows);
              while (norm.length < 5) {
                norm.push({ first_name: '', last_name: '', relationship: '', address: '', phone: '' });
              }
              merged = { ...merged, bus_pickup_rows: norm.slice(0, 5) };
            }
            setFields(merged);
            fieldsBaselineJsonRef.current = JSON.stringify(merged);
            const at = typeof parsed.savedAt === 'number' ? parsed.savedAt : null;
            setDraftSavedAtMs(at != null ? at : Date.now());
            return;
          }
        }
      } catch {
        /* ignore draft parse errors */
      }
    }

    if (savedFormDataKey) {
      try {
        const saved = JSON.parse(savedFormDataKey);
        if (saved && typeof saved === 'object') {
          let merged;
          if (formId === 'fa_12') {
            merged = { ...initialFa12FieldsFromPacket(saved), confirm_agreed: false };
          } else {
            merged = { ...init, ...saved, confirm_agreed: false };
          }
          if (formId === 'fa_03' && merged.bus_pickup_rows) {
            const norm = normalizeFa03BusRows(merged.bus_pickup_rows);
            while (norm.length < 5) {
              norm.push({ first_name: '', last_name: '', relationship: '', address: '', phone: '' });
            }
            merged = { ...merged, bus_pickup_rows: norm.slice(0, 5) };
          }
          if (formId === 'fa_07') {
            merged = {
              ...merged,
              doctor_comm_ack: !!(saved.doctor_comm_ack ?? saved.fa_07_doctor_ack ?? saved.release_ack),
              release_info_ack: !!(saved.release_info_ack ?? saved.fa_07_release_ack ?? saved.release_ack),
            };
          }
          setFields(merged);
          fieldsBaselineJsonRef.current = JSON.stringify(merged);
          setDraftSavedAtMs(null);
          return;
        }
      } catch {
        /* ignore */
      }
    }
    setFields(init);
    fieldsBaselineJsonRef.current = JSON.stringify(init);
    setDraftSavedAtMs(null);
  }, [packet, formId, currentIndex, currentForm, savedFormDataKey, token]);

  const setF = useCallback((k, v) => {
    setFields((prev) => {
      const path = Array.isArray(k) ? k : String(k).split('.');
      if (path.length <= 1) return { ...prev, [k]: v };
      const next = { ...prev };
      let cursor = next;
      path.forEach((part, idx) => {
        if (idx === path.length - 1) {
          cursor[part] = v;
          return;
        }
        const existing = cursor[part];
        cursor[part] = Array.isArray(existing) ? [...existing] : { ...(existing || {}) };
        cursor = cursor[part];
      });
      return next;
    });
  }, []);

  const hasParentFields = formHasParentFields(currentForm);

  useEffect(() => {
    if (!token || !formId || !packet || currentForm?.status === 'signed') return;
    if (!hasParentFields) return;

    const key = packetDraftStorageKey(token, formId);
    const t = setTimeout(() => {
      const baseline = fieldsBaselineJsonRef.current;
      if (baseline == null) return;

      let serializedNow;
      try {
        serializedNow = JSON.stringify(fields);
      } catch {
        return;
      }
      if (serializedNow === baseline) {
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
        setDraftSavedAtMs(null);
        return;
      }
      const savedAt = Date.now();
      try {
        localStorage.setItem(key, JSON.stringify({ savedAt, formState: fields }));
      } catch {
        return;
      }
      setDraftSavedAtMs(savedAt);
    }, 500);

    return () => clearTimeout(t);
  }, [fields, token, formId, packet, currentForm?.status, hasParentFields]);

  const canSubmit = useMemo(
    () =>
      !currentForm
      || currentForm.status === 'signed'
      || !hasParentFields
      || submitFieldsValid(formId, fields),
    [currentForm, hasParentFields, formId, fields],
  );

  const buildFormPayload = () => {
    const { confirm_agreed, ...rest } = fields;
    if (formId === 'fa_12') {
      return { ...buildFa12SubmitPayload(rest), confirm_agreed: !!confirm_agreed };
    }
    if (formId === 'fa_07') {
      return {
        ...rest,
        confirm_agreed: !!confirm_agreed,
        doctor_comm_ack: !!rest.doctor_comm_ack,
        release_info_ack: !!rest.release_info_ack,
        fa_07_doctor_ack: !!rest.doctor_comm_ack,
        fa_07_release_ack: !!rest.release_info_ack,
      };
    }
    return { ...rest, confirm_agreed: !!confirm_agreed };
  };

  const showValidationErrors = useCallback((res) => {
    setFieldErrors(res.fieldErrorMap);
    setSubmitFieldErrors(res.errors.map((e) => e.message));
    requestAnimationFrame(() => {
      const el = res.firstErrorId && document.getElementById(res.firstErrorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget = el.querySelector?.('input:not([type="hidden"]), textarea, select');
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus();
        }
      }
    });
  }, []);

  const flushPacketDraft = useCallback(() => {
    if (!token || !formId || !packet || currentForm?.status === 'signed') return;
    if (!hasParentFields) return;
    try {
      localStorage.setItem(
        packetDraftStorageKey(token, formId),
        JSON.stringify({ savedAt: Date.now(), formState: fields }),
      );
    } catch {
      /* ignore */
    }
  }, [token, formId, packet, hasParentFields, currentForm?.status, fields]);

  const handleGoBack = useCallback(() => {
    flushPacketDraft();
    setFieldErrors({});
    setSubmitFieldErrors([]);
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [flushPacketDraft]);

  const handleGoNext = useCallback(() => {
    if (!packet?.forms) return;
    const form = packet.forms[currentIndex];
    if (!form) return;
    if (form.status === 'signed') {
      setCurrentIndex((i) => Math.min(i + 1, packet.forms.length - 1));
      return;
    }
    if (formHasParentFields(form)) {
      const res = validateForSubmit(form.form_id, fields, packet);
      if (!res.ok) {
        showValidationErrors(res);
        return;
      }
    }
    flushPacketDraft();
    setFieldErrors({});
    setSubmitFieldErrors([]);
    setCurrentIndex((i) => Math.min(i + 1, packet.forms.length - 1));
  }, [packet, currentIndex, fields, flushPacketDraft, showValidationErrors]);

  const handleSubmitForm = async () => {
    if (!packet || !packet.forms || submitting) return;
    const form = packet.forms[currentIndex];
    if (!form || form.status === 'signed') {
      setCurrentIndex((i) => Math.min(i + 1, packet.forms.length));
      return;
    }
    if (hasParentFields) {
      const res = validateForSubmit(form.form_id, fields, packet);
      if (!res.ok) {
        showValidationErrors(res);
        return;
      }
      setFieldErrors({});
      setSubmitFieldErrors([]);
    }
    setSubmitting(true);
    setError('');
    try {
      const body = hasParentFields ? { form_data: buildFormPayload() } : { form_data: {} };
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(form.form_id)}/submit/`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (Array.isArray(data.errors) && data.errors.length) {
          const mappedErrors = data.errors.map((e, idx) => {
            const fieldKey = e?.field || '';
            const message = e?.message || data.detail || 'Failed to submit';
            return {
              id: fieldKey ? packetFieldId(fieldKey) : `packet-field-server-${idx}`,
              fieldKey,
              message,
            };
          });
          const fieldErrorMap = {};
          mappedErrors.forEach((e) => {
            if (e.fieldKey) fieldErrorMap[e.fieldKey] = e.message;
          });
          showValidationErrors({
            ok: false,
            errors: mappedErrors,
            fieldErrorMap,
            firstErrorId: mappedErrors[0]?.id || null,
          });
          return;
        }
        throw new Error(data.detail || 'Failed to submit');
      }
      if (formHasParentFields(form) && token) {
        try {
          localStorage.removeItem(packetDraftStorageKey(token, form.form_id));
        } catch {
          /* ignore */
        }
        setDraftSavedAtMs(null);
      }
      const updated = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/`).then((r) => r.json());
      setPacket(updated);
      setCurrentIndex((i) => Math.min(i + 1, packet.forms.length));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintPacket = useCallback(async () => {
    if (!token || printingPacket) return;
    setPrintError('');
    setPrintingPacket(true);
    try {
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/print/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf, application/json;q=0.9, */*;q=0.8',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Your packet is not ready to print yet. Please complete all forms first.');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'parent_packet_complete.pdf';
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      setPrintError(e?.message || 'Unable to open your packet copy right now. Please try again.');
    } finally {
      setPrintingPacket(false);
    }
  }, [token, printingPacket]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !packet) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This link is invalid or has expired. Please contact the school.
        </Alert>
      </Box>
    );
  }

  if (!packet || !packet.forms || packet.forms.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
        <Typography>No forms to display.</Typography>
      </Box>
    );
  }

  const total = packet.forms.length;
  const allSigned = packet.forms.every((f) => f.is_complete === true || f.status === 'signed');

  if (allSigned || currentIndex >= total) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, fontSize: '1.5rem' }}>
          Thank you!
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', mb: 3 }}>
          All forms have been submitted.
        </Typography>
        {!!printError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {printError}
          </Alert>
        )}
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handlePrintPacket}
          disabled={printingPacket}
          sx={{ py: 1.5, px: 3, fontSize: '1rem' }}
        >
          {printingPacket ? 'Preparing PDF…' : 'Print / Save My Packet'}
        </Button>
      </Box>
    );
  }

  const submitLabel = hasParentFields ? 'Submit & continue' : 'Mark as reviewed & continue';
  const informational = isInformationalPacketForm(formId);
  const textOnlyInfo = informational && informationalTextOnlyForm(formId);
  const formIsSigned = currentForm?.status === 'signed';

  const formTextScroll = (currentForm.form_text || '').trim() ? (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Form letter
      </Typography>
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          maxHeight: 400,
          overflowY: 'auto',
          fontSize: '14px',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          color: 'text.primary',
        }}
      >
        {currentForm.form_text}
      </Box>
      <CalendarFormActions formId={formId} siteSlug={packetSiteSlug} token={token} />
    </Paper>
  ) : null;

  return (
    <Box
      data-na-toggle-enabled={naToggleEnabledForSite ? '1' : '0'}
      data-site-slug={packetSiteSlug || 'unknown'}
      sx={{ maxWidth: 720, mx: 'auto', p: 2, minHeight: '80vh' }}
    >
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>
        {packet.student_first_name && `Hi ${packet.student_first_name}, `}
        Parent Packet — {packet.school_year}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {!familyPickerExpanded ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2">
              <strong>Editing as:</strong>{' '}
              {familyStructureEditingLabel(familyFlagsFromPacket(packet))}
            </Typography>
            <Button size="small" onClick={() => setFamilyPickerExpanded(true)}>
              Change
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Who completes this packet? (check all that apply)
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!fsDraft.has_mother}
                  onChange={(e) => setFsDraft((d) => ({ ...d, has_mother: e.target.checked }))}
                />
              )}
              label="Mother"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!fsDraft.has_father}
                  onChange={(e) => setFsDraft((d) => ({ ...d, has_father: e.target.checked }))}
                />
              )}
              label="Father"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!fsDraft.has_guardian}
                  onChange={(e) => setFsDraft((d) => ({ ...d, has_guardian: e.target.checked }))}
                />
              )}
              label="Guardian"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!fsDraft.is_single_parent}
                  onChange={(e) => setFsDraft((d) => ({ ...d, is_single_parent: e.target.checked }))}
                />
              )}
              label="Single-parent household"
              sx={{ display: 'block', mt: 0.5 }}
            />
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button variant="contained" size="small" onClick={saveFamilyStructure} disabled={familySaving}>
                {familySaving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setFsDraft(familyFlagsFromPacket(packet));
                  setFamilyPickerExpanded(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Paper>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Form {currentIndex + 1} of {total}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={((currentIndex + 1) / total) * 100}
        sx={{ mb: 2, height: 8, borderRadius: 1 }}
      />
      <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
        {currentForm.form_name}
      </Typography>

      {textOnlyInfo && (
        <Paper sx={{ p: 3, mb: 3 }}>
          {currentForm.status === 'signed' ? (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You have already completed this form.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleGoBack}
                  disabled={submitting || currentIndex === 0}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleGoNext}
                  disabled={submitting || currentIndex >= packet.forms.length - 1}
                  sx={{ flex: 1 }}
                >
                  Next
                </Button>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <Box
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 400,
                  overflowY: 'auto',
                  mb: 3,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                }}
              >
                {currentForm.form_text || 'Loading…'}
              </Box>
              <CalendarFormActions formId={formId} siteSlug={packetSiteSlug} token={token} />
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleGoBack}
                  disabled={submitting || currentIndex === 0}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleGoNext}
                  disabled={submitting || currentIndex >= packet.forms.length - 1}
                  sx={{ flex: 1 }}
                >
                  Next
                </Button>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {submitting ? 'Submitting…' : submitLabel}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Fillable / signature forms: letter text (same API as fa_01) + fields; no in-flow PDF embed */}
      {!informational && (
        <>
          {formTextScroll}
          <Paper sx={{ p: 3, mb: 2 }}>
            {formIsSigned && (
              <Typography color="text.secondary" sx={{ mb: hasParentFields ? 2 : 0 }}>
                You have already completed this form. Your entries are shown below for review.
              </Typography>
            )}
            {!formIsSigned && (
              <>
                {hasParentFields && (
                  <>
                    <Typography sx={{ mb: 1, fontWeight: 600 }}>Your responses</Typography>
                    {shouldShowRequiredFieldsCaption(hasParentFields, informational) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Fields marked with * are required.
                      </Typography>
                    )}
                    <FormFields
                      formId={formId}
                      fields={fields}
                      setF={setF}
                      packet={packet}
                      fieldErrors={fieldErrors}
                      clearFieldError={clearFieldError}
                      readOnly={false}
                    />
                    <Box
                      id={packetFieldId('confirm_agreed')}
                      sx={{
                        mt: 2,
                        ...(fieldErrors.confirm_agreed
                          ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 }
                          : {}),
                      }}
                    >
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={!!fields.confirm_agreed}
                            onChange={(e) => {
                              clearFieldError('confirm_agreed');
                              setF('confirm_agreed', e.target.checked);
                            }}
                            color={fieldErrors.confirm_agreed ? 'error' : 'primary'}
                          />
                        )}
                        label={(
                          <>
                            {(formId === 'fa_12' || formId === 'fa_16' || formId === 'fa_17') ? 'I have filled this out to the best of my ability.' : 'I have read the form above and confirm my entries are accurate'}
                            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
                          </>
                        )}
                        sx={{ display: 'block' }}
                      />
                      {!!fieldErrors.confirm_agreed && (
                        <Typography variant="caption" color="error" component="div" sx={{ pl: 4 }}>
                          {fieldErrors.confirm_agreed}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
                {!hasParentFields && (
                  <Typography sx={{ mb: 2 }}>
                    Review the letter above, then continue.
                  </Typography>
                )}
              </>
            )}
            {formIsSigned && hasParentFields && (
              <FormFields
                formId={formId}
                fields={fields}
                setF={setF}
                packet={packet}
                fieldErrors={{}}
                clearFieldError={clearFieldError}
                readOnly
              />
            )}
          </Paper>

          {hasParentFields && submitFieldErrors.length > 0 && !formIsSigned && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitFieldErrors([])}>
              {submitFieldErrors.map((msg, idx) => (
                <Typography key={`${msg}-${idx}`} component="div" variant="body2">
                  {msg}
                </Typography>
              ))}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button
              variant="outlined"
              size="medium"
              onClick={handleGoBack}
              disabled={submitting || currentIndex === 0}
              sx={{ flex: 1 }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="medium"
              onClick={handleGoNext}
              disabled={submitting || currentIndex >= packet.forms.length - 1}
              sx={{ flex: 1 }}
            >
              Next
            </Button>
          </Box>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmitForm}
            disabled={submitting}
            sx={{ mb: hasParentFields && draftSavedAtMs != null ? 0.5 : 3, py: 1.5, fontSize: '1rem' }}
          >
            {submitting ? 'Submitting…' : formIsSigned ? 'Continue' : submitLabel}
          </Button>
          {hasParentFields && !formIsSigned && !canSubmit && submitFieldErrors.length === 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mb: 2 }}>
              Complete the required items above to submit.
            </Typography>
          )}
          {hasParentFields && draftSavedAtMs != null && (
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              sx={{ textAlign: 'center', mb: 3 }}
            >
              Saved {new Date(draftSavedAtMs).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
